<template>
    <div class="relative w-full">
        <RedesignBaseSelect
            v-model="localModel"
            :loading="loading"
            :data="layoutList"
            :config="{
                labelKey: 'LayoutName',
                valueKey: 'LayoutID'
            }"
            :multiple="false"
            full-width
            class="redesign-layout-select"
        >
            <template #prefix>
                <span
                    v-if="localModel"
                    class="prefix-input-tag"
                    :style="prefixStyles"
                >
                    <span class="primary-example" />
                    <span class="font-example">Aa</span>
                </span>
            </template>
            <template #option-item="{ option }">
                <div class="flex items-center gap-2">
                    <span
                        class="prefix-input-tag flex-shrink-0"
                        :style="getOptionStyles(option.LayoutParametersList || [])"
                    >
                        <span class="primary-example" />
                        <span class="font-example">Aa</span>
                    </span>
                    <span v-if="!option.LayoutAccountID">{{ getLayoutTranslateName(option.LayoutName) }}</span>
                    <span v-else>{{ option.LayoutName }}</span>
                </div>
            </template>
        </RedesignBaseSelect>
        <div
            v-if="loading"
            class="absolute w-full h-full left-0 top-0"
        >
            <div class="main-bg-light w-full h-full opacity-70" />
            <RedesignBaseSpinner class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
    </div>
</template>

<script setup lang="ts">
import RedesignBaseSelect from '@/components/RedesignBaseComponents/Inputs/RedesignBaseSelect.vue'
import { useVModel } from '@vueuse/core'
import { ILayoutItem, ILayoutParametersList } from '@/types/dashboard'
import { computed, ref, watch } from 'vue'
import { LayoutApi } from '@/api/layoutApi'
import { selectedAccountID } from '@/composables/globalData/useEntitiesData'
import { ENABLED_STATUS_ID } from '@/views/DashboardSettings/LayoutManagement/layout-management'
import { getLayoutParameterByName } from '@/composables/globalData/useLayouts'
import { layoutDefaultColors } from '@/enum/colors'
import i18n from '@/i18n'
import RedesignBaseSpinner from '@/components/RedesignBaseComponents/RedesignBaseSpinner.vue'

const props = defineProps<{
    value?: number
    excluded?: Array<number>
}>()

const emit = defineEmits<{
    (e: 'input', value?: number): void
    (e: 'update:value', value?: number): void
    (e: 'on-change-layout', value: ILayoutItem): void
    (e: 'upload-layouts', value: Array<ILayoutItem>): void
    (e: 'ready'): void
}>()

const loading = ref(false)
const layoutList = ref<Array<ILayoutItem>>([])
const localModel = useVModel(props, 'value', emit, { eventName: 'input' })

const selectedLayout = computed(() => {
    return layoutList.value.find(layout => layout.LayoutID === localModel.value)
})
const prefixStyles = computed(() => {
    return getOptionStyles(selectedLayout.value?.LayoutParametersList || [])
})

function getLayoutTranslateName (name: string) {
    const translateKey = `layout.${name.toLowerCase().replace(/\s/g, '_')}`
    if (i18n.te(translateKey)) {
        return i18n.t(translateKey)
    }
    return name
}

function getOptionStyles (layoutParametersList: Array<ILayoutParametersList>) {
    //TODO: '--border-color': 'var(--ui-lines)' need to change it
    const colorPrimary = getLayoutParameterByName(layoutParametersList, 'ColorPrimary', 'Value')
    const colorBackground = getLayoutParameterByName(layoutParametersList, 'MainBgColor', 'Value')
    // const colorDefaultBackground = getLayoutParameterByName(layoutParametersList, 'ColorBackground', 'Value')
    const colorFrames = getLayoutParameterByName(layoutParametersList, 'ColorFrames', 'Value')
    const colorText = getLayoutParameterByName(layoutParametersList, 'MainTextColor', 'Value')
    return {
        '--border-color': colorFrames || 'var(--ui-lines)',
        '--bg-color': colorBackground || layoutDefaultColors.mainBgColor,
        '--text-color': colorText || layoutDefaultColors.mainTextColor,
        '--primary-color': colorPrimary || layoutDefaultColors.primary
    }
}

async function initData (callback?: () => void) {
    loading.value = true

    try {
        const response = await LayoutApi
            .get({ LayoutAccountID: selectedAccountID.value })
        const excludedIds = props.excluded || []
        layoutList.value = response?.filter(layout => layout.LayoutStatusID === ENABLED_STATUS_ID && !excludedIds.includes(layout.LayoutID || 0))

        emit('on-change-layout', selectedLayout.value)
        emit('upload-layouts', layoutList.value)
        emit('ready')

        if (callback) {
            callback()
        }
    } finally {
        return loading.value = false
    }
}

watch(
    selectedLayout,
    (val) => {
        emit('on-change-layout', val)
    }
)

initData()

defineExpose({
    refresh: initData,
    getSelectedData: () => selectedLayout.value,
    getLayouts: () => [ ...layoutList.value ],
    getLayoutById: (id: number) => layoutList.value.find(layout => layout.LayoutID === id)
})
</script>

<style lang="scss">
.redesign-layout-select {
    @apply relative;
    .el-input .el-input__inner {
        @apply ltr:pl-14 rtl:pr-14;
    }
}
.prefix-input-tag {
    @apply border rounded-xl flex gap-1 px-1.5 items-center py-[3px];
    background-color: var(--bg-color);
    border-color: var(--border-color);
    .primary-example {
        @apply w-2 h-2 rounded-full;
        background-color: var(--primary-color);
    }
    .font-example {
        @apply text-sm font-semibold leading-none;
        color: var(--text-color);
    }
}
</style>
